import type { IntlShape } from 'react-intl';

import type { FilterValues } from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { formatNationalId } from '@src/packages/utils/nationalId';
import { get } from '@src/packages/utils/storage';

import {
  LOW_BATTERY_THRESHOLD,
  MOBILE_USER_SEARCH_THRESHOLD,
  POSITION_STALE_TIMEOUT_MINUTES,
  USER_CONNECTION_TIMEOUT_MINUTES,
  USER_LIST_PAGINATION_HEIGHT,
} from './constants';
import { isCurrentPosition, resolveTrackingDisplay } from './utils/trackingDisplay';
import {
  getStalledFromFilters,
  getLowBatteryFromFilters,
  getStatusFromFilters,
  getTextFromFilters,
} from './activeUserFilterConfig';
import type {
  ActiveUserKpis,
  ActiveUserListItem,
  ComplianceByUser,
  LiveStatusByUser,
  RosterUser,
  SortState,
  UserDailyStats,
} from './types';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export const getUserListPageSize = (
  total: number,
  availableHeight: number,
  headerHeight: number,
  rowHeight: number,
): number => {
  const rowsWithoutPagination = Math.max(1, Math.floor((availableHeight - headerHeight) / rowHeight));
  return total > rowsWithoutPagination
    ? Math.max(1, Math.floor((availableHeight - headerHeight - USER_LIST_PAGINATION_HEIGHT) / rowHeight))
    : rowsWithoutPagination;
};

export const isUserConnectedByDeviceTime = (
  deviceTime: number | null | undefined,
  nowMs: number = Date.now(),
): boolean => {
  if (!deviceTime) {
    return false;
  }
  return nowMs - deviceTime <= USER_CONNECTION_TIMEOUT_MINUTES * MINUTE_MS;
};

export const isPositionStale = (
  positionTime: number | null | undefined,
  nowMs: number = Date.now(),
): boolean => {
  if (!positionTime) {
    return true;
  }
  return nowMs - positionTime > POSITION_STALE_TIMEOUT_MINUTES * MINUTE_MS;
};

export const getConfiguredTimeZone = (): string => {
  const stored = get('timeZone') as string | null | undefined;
  return stored || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
};

/** `yyyy-MM-dd` key for "today" in the configured timezone (matches backend dailyStats keys). */
export const getTodayDateKey = (timeZone: string, nowMs: number = Date.now()): string =>
  // en-CA locale formats dates as YYYY-MM-DD.
  new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(nowMs);

/**
 * Today's event count from the backend-maintained dailyStats counters.
 * Returns null when the backend has not populated counters for this user yet,
 * so the UI can distinguish "0 events" from "no data".
 */
export const getEventsToday = (
  dailyStats: UserDailyStats | undefined,
  todayKey: string,
): number | null => {
  if (!dailyStats) {
    return null;
  }
  return dailyStats[todayKey]?.events ?? 0;
};

const buildDisplayName = (firstName: string, lastName: string): string => {
  const displayName = `${firstName} ${lastName}`.trim();
  return displayName !== '' ? displayName : '-';
};

export interface RelativeTimeParts {
  value: number;
  unit: 'minute' | 'hour' | 'day';
}

/** Coarse relative-time parts for intl.formatRelativeTime (negative = past). */
export const getRelativeTimeParts = (
  timestamp: number,
  nowMs: number = Date.now(),
): RelativeTimeParts => {
  const elapsed = Math.max(0, nowMs - timestamp);
  if (elapsed < HOUR_MS) {
    return { value: -Math.floor(elapsed / MINUTE_MS), unit: 'minute' };
  }
  if (elapsed < DAY_MS) {
    return { value: -Math.floor(elapsed / HOUR_MS), unit: 'hour' };
  }
  return { value: -Math.floor(elapsed / DAY_MS), unit: 'day' };
};

export const formatLastSeen = (
  intl: IntlShape,
  timestamp: number | null,
  nowMs: number = Date.now(),
): string => {
  if (!timestamp) {
    return intl.formatMessage({ id: 'activeUserDashboard.lastSeen.never' });
  }
  const { value, unit } = getRelativeTimeParts(timestamp, nowMs);
  return intl.formatRelativeTime(value, unit, { numeric: 'auto' });
};

export const formatAbsoluteTime = (
  intl: IntlShape,
  timestamp: number | null,
  timeZone: string,
): string | null => {
  if (!timestamp) {
    return null;
  }
  return intl.formatDate(timestamp, {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export interface BuildUserListParams {
  users: RosterUser[];
  statusByUser: LiveStatusByUser;
  complianceByUser: ComplianceByUser;
  userFocusedId?: string | null;
  currentUserId?: string;
  currentUserPhotoURL?: string;
  nowMs: number;
  timeZone: string;
  intl: IntlShape;
}

/** O(n) assembly of list rows from the roster + live status + compliance maps. */
export const buildUserList = ({
  users,
  statusByUser,
  complianceByUser,
  userFocusedId = null,
  currentUserId,
  currentUserPhotoURL,
  nowMs,
  timeZone,
  intl,
}: BuildUserListParams): ActiveUserListItem[] => {
  const todayKey = getTodayDateKey(timeZone, nowMs);

  return users
    .filter((user): user is RosterUser & { id: string } => Boolean(user?.id))
    .map(user => {
      const { id, photoURL = '', divisions, firstName = '', lastName = '', dailyStats } = user;

      const live = statusByUser[id];
      const device = live?.device;
      const position = live?.position;
      const compliance = complianceByUser[id];

      const lastSeenAt = device?.time ?? null;
      const positionAt = position?.t ?? null;
      const trackingDisplay = resolveTrackingDisplay(live?.trackingState);
      const positionCurrent = isCurrentPosition(trackingDisplay);
      const hasCoordinates = positionCurrent && position?.l != null && position?.lg != null;

      return {
        key: id,
        id,
        firstName,
        lastName,
        displayName: buildDisplayName(firstName, lastName),
        idCard: user.idCard ? formatNationalId(user.address?.country, user.idCard) : undefined,
        eventsToday: getEventsToday(dailyStats, todayKey),
        scheduleCompliance: [
          compliance?.compliance.schedule.finished ?? 0,
          compliance?.compliance.schedule.expected ?? 0,
        ] as [number, number],
        taskCompliance: [
          compliance?.compliance.task.finished ?? 0,
          compliance?.compliance.task.expected ?? 0,
        ] as [number, number],
        checkpointRouteCompliance: [
          compliance?.compliance.checkpoint_route.finished ?? 0,
          compliance?.compliance.checkpoint_route.expected ?? 0,
        ] as [number, number],
        delays: Boolean(compliance?.stalled),
        connected: isUserConnectedByDeviceTime(lastSeenAt, nowMs),
        battery: device?.battery ?? null,
        lastSeenAt,
        positionAt,
        positionTimeFormated: formatAbsoluteTime(intl, positionAt, timeZone),
        speed: position?.s ?? 0,
        accuracy: position?.ac ?? 0,
        altitude: position?.a ?? 0,
        positions: hasCoordinates ? ([position!.l!, position!.lg!] as [number, number]) : null,
        positionStale: isPositionStale(positionAt, nowMs),
        trackingDisplay,
        positionCurrent,
        divisions: divisions ?? {},
        photoURL: [
          photoURL,
          user.photoUrl,
          user.avatarURL,
          id === currentUserId ? currentUserPhotoURL : undefined,
        ]
          .find(candidate => typeof candidate === 'string' && candidate.trim())?.trim() ?? '',
        isSelected: userFocusedId === id,
      };
    });
};

export const getFilteredUsers = (
  userList: ActiveUserListItem[],
  filters: FilterValues,
  selectedDivsIds: string[],
): ActiveUserListItem[] => {
  const text = getTextFromFilters(filters).trim().toLowerCase();
  const status = getStatusFromFilters(filters);
  const stalledOnly = getStalledFromFilters(filters);
  const lowBatteryOnly = getLowBatteryFromFilters(filters);

  return userList.filter(user => {
    const userDivisionIds = Object.keys(user.divisions ?? {});

    if (!userDivisionIds.some(divId => selectedDivsIds.includes(divId))) {
      return false;
    }
    if (status !== null && user.connected !== status) {
      return false;
    }
    if (stalledOnly && !user.delays) {
      return false;
    }
    if (lowBatteryOnly && !(user.battery !== null && user.battery <= LOW_BATTERY_THRESHOLD)) {
      return false;
    }
    if (text !== '' && !user.displayName.toLowerCase().includes(text)) {
      return false;
    }
    return true;
  });
};

const complianceRatio = ([finished, expected]: [number, number]): number => {
  if (expected === 0) {
    return -1;
  }
  return finished / expected;
};

export const sortUsers = (
  userList: ActiveUserListItem[],
  sortState: SortState,
): ActiveUserListItem[] => {
  const direction = sortState.order === 'asc' ? 1 : -1;

  const compare = (a: ActiveUserListItem, b: ActiveUserListItem): number => {
    switch (sortState.key) {
      case 'lastSeenAt':
        return ((a.lastSeenAt ?? 0) - (b.lastSeenAt ?? 0)) * direction;
      case 'battery':
        return ((a.battery ?? -1) - (b.battery ?? -1)) * direction;
      case 'scheduleCompliance':
        return (
          (complianceRatio(a.scheduleCompliance) - complianceRatio(b.scheduleCompliance)) *
          direction
        );
      case 'taskCompliance':
        return (
          (complianceRatio(a.taskCompliance) - complianceRatio(b.taskCompliance)) * direction
        );
      case 'checkpointRouteCompliance':
        return (
          (complianceRatio(a.checkpointRouteCompliance) -
            complianceRatio(b.checkpointRouteCompliance)) *
          direction
        );
      case 'eventsToday':
        return ((a.eventsToday ?? -1) - (b.eventsToday ?? -1)) * direction;
      case 'displayName':
      default:
        return a.displayName.localeCompare(b.displayName) * direction;
    }
  };

  return [...userList].sort((a, b) => {
    // Connected users always group first; sort applies within each group.
    if (a.connected !== b.connected) {
      return a.connected ? -1 : 1;
    }
    const result = compare(a, b);
    return result !== 0 ? result : a.displayName.localeCompare(b.displayName);
  });
};

/** Mobile search depends on the roster size, so filters that narrow the list keep it visible. */
export const shouldShowMobileSearch = (isMobile: boolean, rosterSize: number): boolean =>
  isMobile && rosterSize > MOBILE_USER_SEARCH_THRESHOLD;

export const computeKpis = (userList: ActiveUserListItem[]): ActiveUserKpis =>
  userList.reduce<ActiveUserKpis>(
    (acc, user) => ({
      total: acc.total + 1,
      connected: acc.connected + (user.connected ? 1 : 0),
      stalled: acc.stalled + (user.delays ? 1 : 0),
      lowBattery:
        acc.lowBattery +
        (user.battery !== null && user.battery <= LOW_BATTERY_THRESHOLD ? 1 : 0),
    }),
    { total: 0, connected: 0, stalled: 0, lowBattery: 0 },
  );
